<template>
    <nav class="flex flex-col gap-6" aria-label="Staff workspace">
        <section
            v-for="group in groups"
            :key="group.label"
            class="flex flex-col gap-1"
        >
            <h2
                class="px-3 pb-2 text-xs font-medium tracking-wider text-muted-foreground uppercase"
            >
                {{ group.label }}
            </h2>
            <Link
                v-for="item in group.items"
                :key="item.key"
                :href="item.href"
                :aria-current="activeKey === item.key ? 'page' : undefined"
                :class="
                    cn(
                        'flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                        activeKey === item.key
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                "
                prefetch
                @click="emit('navigate')"
            >
                <component
                    :is="icons[item.key] ?? LayoutDashboard"
                    class="size-4 shrink-0"
                    :stroke-width="1.8"
                    aria-hidden="true"
                />
                <span>{{ item.label }}</span>
                <span
                    v-if="activeKey === item.key"
                    class="ms-auto size-1.5 shrink-0 rounded-full bg-primary"
                    aria-hidden="true"
                />
            </Link>
        </section>
    </nav>
</template>

<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import {
    Activity,
    BookOpen,
    CreditCard,
    FileText,
    Image,
    LayoutDashboard,
    LifeBuoy,
    MessageSquare,
    ShieldCheck,
    Tags,
    Users,
    UsersRound,
} from '@lucide/vue';
import { computed, type Component } from 'vue';
import type { OrgNavItem } from '@/lib/orgNavigation';
import { cn } from '@/lib/utils';

const props = defineProps<{ items: OrgNavItem[]; activeKey: string | null }>();
const emit = defineEmits<{ navigate: [] }>();
const icons: Record<string, Component> = {
    today: LayoutDashboard,
    customers: Users,
    feedback: MessageSquare,
    support: LifeBuoy,
    finance: CreditCard,
    operations: Activity,
    posts: BookOpen,
    pages: FileText,
    media: Image,
    taxonomy: Tags,
    staff: UsersRound,
    roles: ShieldCheck,
};
const definitions = [
    {
        label: 'Workspace',
        keys: [
            'today',
            'customers',
            'support',
            'feedback',
            'finance',
            'operations',
        ],
    },
    { label: 'Publishing', keys: ['posts', 'pages', 'media', 'taxonomy'] },
    { label: 'Organization', keys: ['staff', 'roles'] },
];
const groups = computed(() =>
    definitions
        .map((group) => ({
            label: group.label,
            items: group.keys.flatMap((key) =>
                props.items.filter((item) => item.key === key),
            ),
        }))
        .filter((group) => group.items.length > 0),
);
</script>
